import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import {
  obtenerPresupuesto, obtenerHistorial,
  actualizarPeriodos, distribuirPresupuesto,
  cambiarEstado, clonarPresupuesto, eliminarPresupuesto,
} from "../api"
import {
  formatCurrency, formatNumber, formatFecha, formatFechaHora,
  estadoConfig, transicionesPosibles, periodicidadLabel,
  muestraMonto, muestraUnidades,
} from "../helpers"

const TABS = [
  { id: "resumen",    label: "Resumen",    icon: "mdi-view-dashboard-outline" },
  { id: "periodos",   label: "Periodos",   icon: "mdi-calendar-range" },
  { id: "categorias", label: "Categorías", icon: "mdi-tag-multiple-outline" },
  { id: "versiones",  label: "Versiones",  icon: "mdi-source-branch" },
  { id: "historial",  label: "Historial",  icon: "mdi-history" },
]

const DrawerDetalle = ({ abierto, presupuestoId, onCerrar, onCambio }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tab, setTab] = useState("resumen")

  // Edit state para periodos
  const [periodosEdit, setPeriodosEdit] = useState({})
  const [savingPeriodos, setSavingPeriodos] = useState(false)

  // Distribución
  const [showDistribuir, setShowDistribuir] = useState(false)
  const [metodoDist, setMetodoDist] = useState("LINEAL")
  const [porcentajesDist, setPorcentajesDist] = useState("")
  const [montoNuevo, setMontoNuevo] = useState("")
  const [distribuyendo, setDistribuyendo] = useState(false)

  // Cambio de estado
  const [cambiandoEstado, setCambiandoEstado] = useState(false)

  // Clonar
  const [showClonar, setShowClonar] = useState(false)
  const [clonNombre, setClonNombre] = useState("")
  const [clonNotas, setClonNotas] = useState("")
  const [clonando, setClonando] = useState(false)

  // Historial
  const [historial, setHistorial] = useState([])
  const [loadingHistorial, setLoadingHistorial] = useState(false)

  const cargar = useCallback(async () => {
    if (!abierto || !presupuestoId) return
    setLoading(true)
    setError("")
    setData(null)
    setPeriodosEdit({})
    try {
      const r = await obtenerPresupuesto(presupuestoId)
      const body = r.data
      if (body?.success === false) throw new Error(body.message || "Error en el servidor")
      setData(body?.data || body)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar el detalle.")
    } finally {
      setLoading(false)
    }
  }, [abierto, presupuestoId])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => {
    if (abierto) {
      setTab("resumen")
      document.body.style.overflow = "hidden"
      return () => { document.body.style.overflow = "" }
    }
  }, [abierto])

  const cargarHistorial = useCallback(async () => {
    if (!presupuestoId) return
    setLoadingHistorial(true)
    try {
      const r = await obtenerHistorial(presupuestoId)
      const body = r.data
      setHistorial(body?.data || [])
    } catch {
      setHistorial([])
    } finally {
      setLoadingHistorial(false)
    }
  }, [presupuestoId])

  useEffect(() => {
    if (tab === "historial" && historial.length === 0) cargarHistorial()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const estado = data?.estado
  const cfg = estadoConfig(estado)
  const editable = cfg.editable
  const transiciones = transicionesPosibles(estado)
  const tipoMedicion = data?.tipoMedicion || "MONTO"

  // ──── Periodos handlers ────
  const onPeriodoChange = (periodoId, campo, valor) => {
    setPeriodosEdit(prev => ({
      ...prev,
      [periodoId]: { ...(prev[periodoId] || {}), [campo]: valor },
    }))
  }
  const periodosEditCount = Object.keys(periodosEdit).length

  const guardarPeriodos = async () => {
    if (!periodosEditCount) return
    setSavingPeriodos(true)
    try {
      const lista = Object.entries(periodosEdit).map(([id, cambios]) => {
        const p = { id }
        if (cambios.montoObjetivo !== undefined) p.montoObjetivo = Number(cambios.montoObjetivo) || 0
        if (cambios.unidadesObjetivo !== undefined) p.unidadesObjetivo = Number(cambios.unidadesObjetivo) || 0
        if (cambios.notas !== undefined) p.notas = cambios.notas
        return p
      })
      await actualizarPeriodos(presupuestoId, lista)
      setPeriodosEdit({})
      await cargar()
      onCambio?.()
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Error al guardar periodos.")
    } finally {
      setSavingPeriodos(false)
    }
  }

  const ejecutarDistribucion = async () => {
    setDistribuyendo(true)
    try {
      const body = { metodo: metodoDist }
      if (montoNuevo) body.montoTotal = Number(montoNuevo)
      if (metodoDist === "MANUAL") {
        const arr = porcentajesDist.split(/[,\s]+/).filter(Boolean).map(s => Number(s))
        body.porcentajes = arr
      }
      await distribuirPresupuesto(presupuestoId, body)
      setShowDistribuir(false)
      setPorcentajesDist("")
      setMontoNuevo("")
      setMetodoDist("LINEAL")
      await cargar()
      onCambio?.()
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Error al distribuir.")
    } finally {
      setDistribuyendo(false)
    }
  }

  const ejecutarCambioEstado = async (nuevo) => {
    if (!window.confirm(`¿Cambiar estado a ${nuevo}?`)) return
    setCambiandoEstado(true)
    try {
      await cambiarEstado(presupuestoId, nuevo)
      await cargar()
      onCambio?.()
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Error al cambiar estado.")
    } finally {
      setCambiandoEstado(false)
    }
  }

  const ejecutarClonar = async () => {
    setClonando(true)
    try {
      const r = await clonarPresupuesto(presupuestoId, {
        nombre: clonNombre || undefined,
        notas: clonNotas || undefined,
      })
      setShowClonar(false)
      setClonNombre("")
      setClonNotas("")
      onCambio?.()
      // Abrir la nueva versión
      const nuevoId = r.data?.data?.id
      if (nuevoId) {
        // Notificamos al padre para que cambie de presupuesto activo
        onCambio?.(nuevoId)
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Error al clonar.")
    } finally {
      setClonando(false)
    }
  }

  const ejecutarEliminar = async () => {
    if (!window.confirm("¿Eliminar este presupuesto? (soft delete)")) return
    try {
      await eliminarPresupuesto(presupuestoId)
      onCambio?.()
      onCerrar()
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Error al eliminar.")
    }
  }

  // Totales calculados desde periodos editados (preview)
  const totalesPreview = useMemo(() => {
    if (!data?.periodos) return null
    const sumMonto = data.periodos.reduce((a, p) => {
      const e = periodosEdit[p.id]
      const m = e?.montoObjetivo !== undefined ? Number(e.montoObjetivo) : parseFloat(p.montoObjetivo || 0)
      return a + (isFinite(m) ? m : 0)
    }, 0)
    const sumUnidades = data.periodos.reduce((a, p) => {
      const e = periodosEdit[p.id]
      const u = e?.unidadesObjetivo !== undefined ? Number(e.unidadesObjetivo) : parseFloat(p.unidadesObjetivo || 0)
      return a + (isFinite(u) ? u : 0)
    }, 0)
    return { sumMonto, sumUnidades }
  }, [data, periodosEdit])

  if (!abierto) return null

  return (
    <>
      <div onClick={onCerrar} style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        zIndex: 1040, backdropFilter: "blur(2px)",
      }} />
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(1080px, 95vw)",
        background: "#f8fafc", zIndex: 1041,
        display: "flex", flexDirection: "column",
        boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
        animation: "slideInRight .25s ease",
      }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(40px); opacity: .6 } to { transform: translateX(0); opacity: 1 } }`}</style>

        {/* Header */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "14px 20px" }}>
          <div className="d-flex align-items-center gap-14 flex-wrap" style={{ gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: `${cfg.color}1a`, color: cfg.color,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, flexShrink: 0,
            }}>
              <i className={`mdi ${cfg.icon}`} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
                Presupuesto · v{data?.version || 1}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
                {data?.nombre || "Cargando…"}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {data?.empresa?.nombre || ""} {data?.sucursal?.nombre ? `· ${data.sucursal.nombre}` : data?.empresa ? "· Nivel empresa" : ""}
              </div>
            </div>
            <span style={{
              padding: "4px 12px", borderRadius: 999,
              background: cfg.bg, color: cfg.color,
              fontSize: 12, fontWeight: 700,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
              <i className={`mdi ${cfg.icon}`} />
              {cfg.label}
            </span>
            <button type="button" onClick={onCerrar} style={{
              background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
              color: "#475569", cursor: "pointer", padding: "6px 10px", fontSize: 13,
            }}>
              <i className="mdi mdi-close me-1" />Cerrar
            </button>
          </div>

          {/* Botones de acción según estado */}
          {data && (
            <div className="d-flex gap-2 mt-3 flex-wrap" style={{ gap: 8 }}>
              {transiciones.map(t => {
                const tcfg = estadoConfig(t)
                return (
                  <button key={t} type="button"
                    onClick={() => ejecutarCambioEstado(t)}
                    disabled={cambiandoEstado}
                    style={{
                      padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                      background: tcfg.bg, color: tcfg.color, border: `1px solid ${tcfg.color}40`, cursor: "pointer",
                    }}>
                    <i className={`mdi ${tcfg.icon} me-1`} />
                    Cambiar a {tcfg.label}
                  </button>
                )
              })}
              {(estado === "ACTIVO" || estado === "CERRADO") && (
                <button type="button" onClick={() => setShowClonar(true)} disabled={clonando}
                  className="btn btn-sm btn-outline-primary">
                  <i className="mdi mdi-content-duplicate me-1" /> Nueva versión
                </button>
              )}
              {estado !== "ACTIVO" && (
                <button type="button" onClick={ejecutarEliminar} className="btn btn-sm btn-outline-danger">
                  <i className="mdi mdi-trash-can-outline me-1" /> Eliminar
                </button>
              )}
              {editable && (
                <button type="button" onClick={() => setShowDistribuir(s => !s)} className="btn btn-sm btn-outline-secondary ms-auto">
                  <i className="mdi mdi-chart-pie me-1" /> Distribuir montos
                </button>
              )}
            </div>
          )}

          {/* Distribución panel */}
          {showDistribuir && (
            <div style={{
              marginTop: 10, padding: 10, background: "#f1f5f9", borderRadius: 8,
              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
            }}>
              <select value={metodoDist} onChange={e => setMetodoDist(e.target.value)}
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid #cbd5e1" }}>
                <option value="LINEAL">Lineal (partes iguales)</option>
                <option value="MANUAL">Manual (porcentajes)</option>
              </select>
              <input type="number" placeholder="Monto total (opcional)"
                value={montoNuevo} onChange={e => setMontoNuevo(e.target.value)}
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid #cbd5e1", width: 200 }} />
              {metodoDist === "MANUAL" && (
                <input type="text" placeholder="Porcentajes separados por coma (ej: 5,5,8,10,10,12)"
                  value={porcentajesDist} onChange={e => setPorcentajesDist(e.target.value)}
                  style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid #cbd5e1", flex: 1, minWidth: 280 }} />
              )}
              <button type="button" className="btn btn-sm btn-primary"
                onClick={ejecutarDistribucion} disabled={distribuyendo}>
                {distribuyendo ? <Spinner size="sm" /> : "Aplicar"}
              </button>
            </div>
          )}

          {/* Clonar panel */}
          {showClonar && (
            <div style={{
              marginTop: 10, padding: 10, background: "#eff6ff", borderRadius: 8,
              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
            }}>
              <input type="text" placeholder="Nombre nueva versión (opcional)"
                value={clonNombre} onChange={e => setClonNombre(e.target.value)}
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid #93c5fd", flex: 1, minWidth: 200 }} />
              <input type="text" placeholder="Notas (opcional)"
                value={clonNotas} onChange={e => setClonNotas(e.target.value)}
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid #93c5fd", flex: 1, minWidth: 200 }} />
              <button type="button" className="btn btn-sm btn-primary"
                onClick={ejecutarClonar} disabled={clonando}>
                {clonando ? <Spinner size="sm" /> : "Clonar"}
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowClonar(false)}>Cancelar</button>
            </div>
          )}
        </div>

        {/* Sub-tabs */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 14px", display: "flex", gap: 2, overflowX: "auto" }}>
          {TABS.map(t => {
            const activo = tab === t.id
            return (
              <button key={t.id} type="button" onClick={() => setTab(t.id)} style={{
                background: "none", border: "none", padding: "12px 14px",
                fontSize: 12, fontWeight: 600,
                color: activo ? "#0f172a" : "#64748b",
                borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
              }}>
                <i className={`mdi ${t.icon}`} />{t.label}
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {loading && (
            <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
              <Spinner color="primary" />
              <div className="mt-2">Cargando detalle…</div>
            </div>
          )}

          {error && !loading && (
            <div style={{ padding: "12px 16px", background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 10 }}>
              <i className="mdi mdi-alert-circle me-1" />{error}
            </div>
          )}

          {data && !loading && !error && (
            <>
              {/* TAB RESUMEN */}
              {tab === "resumen" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {muestraMonto(tipoMedicion) && (
                      <div style={statBox}>
                        <div style={statLabel}>Monto total</div>
                        <div style={{ ...statValue, color: "#10b981" }}>{formatCurrency(data.montoTotal, data.moneda)}</div>
                      </div>
                    )}
                    {muestraUnidades(tipoMedicion) && (
                      <div style={statBox}>
                        <div style={statLabel}>Unidades total</div>
                        <div style={{ ...statValue, color: "#3b82f6" }}>{formatNumber(data.unidadesTotal, 0)}</div>
                      </div>
                    )}
                    <div style={statBox}>
                      <div style={statLabel}>Periodos</div>
                      <div style={statValue}>{data._count?.periodos ?? data.periodos?.length ?? 0}</div>
                    </div>
                    <div style={statBox}>
                      <div style={statLabel}>Categorías</div>
                      <div style={statValue}>{data._count?.categorias ?? data.categorias?.length ?? 0}</div>
                    </div>
                  </div>

                  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
                    <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
                      <i className="mdi mdi-information-outline me-2 text-primary" />
                      Datos del presupuesto
                    </h6>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, fontSize: 13 }}>
                      <Field label="Año">{data.anio}</Field>
                      <Field label="Periodicidad">{periodicidadLabel(data.periodicidad)}</Field>
                      <Field label="Tipo medición">{data.tipoMedicion}</Field>
                      <Field label="Moneda">{data.moneda}</Field>
                      <Field label="Fecha inicio">{formatFecha(data.fechaInicio)}</Field>
                      <Field label="Fecha fin">{formatFecha(data.fechaFin)}</Field>
                      <Field label="Versión">{data.version}{data.presupuestoPadreId ? ` (hija de v1)` : " (raíz)"}</Field>
                      <Field label="Aprobado por">{data.aprobadoPor || "—"}</Field>
                      <Field label="Aprobado en">{formatFechaHora(data.aprobadoEn)}</Field>
                      <Field label="Creado">{formatFechaHora(data.createdAt)}</Field>
                    </div>
                    {data.descripcion && (
                      <div style={{ marginTop: 10, padding: 10, background: "#f8fafc", borderRadius: 6, fontSize: 12, color: "#475569" }}>
                        <strong>Descripción:</strong> {data.descripcion}
                      </div>
                    )}
                    {data.notas && (
                      <div style={{ marginTop: 6, padding: 10, background: "#fffbeb", borderRadius: 6, fontSize: 12, color: "#78350f" }}>
                        <strong>Notas:</strong> {data.notas}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB PERIODOS */}
              {tab === "periodos" && (
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap" style={{ gap: 8 }}>
                    <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
                      <i className="mdi mdi-calendar-range me-2 text-primary" />
                      {data.periodos?.length || 0} periodos · {periodicidadLabel(data.periodicidad)}
                    </h6>
                    {editable && periodosEditCount > 0 && (
                      <button type="button" className="btn btn-sm btn-primary"
                        onClick={guardarPeriodos} disabled={savingPeriodos}>
                        {savingPeriodos ? <Spinner size="sm" /> : (<><i className="mdi mdi-content-save me-1" /> Guardar {periodosEditCount} cambio{periodosEditCount > 1 ? "s" : ""}</>)}
                      </button>
                    )}
                  </div>
                  {!editable && (
                    <div style={{ padding: 10, marginBottom: 10, background: "#fef3c7", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
                      <i className="mdi mdi-lock-outline me-1" />
                      Este presupuesto está <strong>{cfg.label}</strong> y no es editable. Crea una nueva versión para hacer cambios.
                    </div>
                  )}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={th}>#</th>
                          <th style={th}>Etiqueta</th>
                          <th style={th}>Desde</th>
                          <th style={th}>Hasta</th>
                          {muestraMonto(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Monto objetivo</th>}
                          {muestraUnidades(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Unidades obj.</th>}
                          <th style={th}>Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.periodos || []).map((p, i) => {
                          const edit = periodosEdit[p.id] || {}
                          const dirty = Object.keys(edit).length > 0
                          return (
                            <tr key={p.id} style={{ background: dirty ? "#fffbeb" : (i % 2 ? "#fafbfc" : "#fff") }}>
                              <td style={td}>{p.numeroPeriodo}</td>
                              <td style={{ ...td, fontWeight: 600 }}>{p.etiqueta}</td>
                              <td style={td}>{formatFecha(p.fechaInicio)}</td>
                              <td style={td}>{formatFecha(p.fechaFin)}</td>
                              {muestraMonto(tipoMedicion) && (
                                <td style={{ ...td, textAlign: "right" }}>
                                  {editable ? (
                                    <input type="number"
                                      value={edit.montoObjetivo ?? p.montoObjetivo ?? 0}
                                      onChange={e => onPeriodoChange(p.id, "montoObjetivo", e.target.value)}
                                      style={inputStyle} />
                                  ) : formatCurrency(p.montoObjetivo, data.moneda)}
                                </td>
                              )}
                              {muestraUnidades(tipoMedicion) && (
                                <td style={{ ...td, textAlign: "right" }}>
                                  {editable ? (
                                    <input type="number"
                                      value={edit.unidadesObjetivo ?? p.unidadesObjetivo ?? 0}
                                      onChange={e => onPeriodoChange(p.id, "unidadesObjetivo", e.target.value)}
                                      style={inputStyle} />
                                  ) : formatNumber(p.unidadesObjetivo, 2)}
                                </td>
                              )}
                              <td style={td}>
                                {editable ? (
                                  <input type="text"
                                    value={edit.notas ?? p.notas ?? ""}
                                    onChange={e => onPeriodoChange(p.id, "notas", e.target.value)}
                                    style={{ ...inputStyle, minWidth: 160 }}
                                    placeholder="—" />
                                ) : (p.notas || <span style={{ color: "#cbd5e1" }}>—</span>)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                          <td style={td} colSpan={4}>TOTAL</td>
                          {muestraMonto(tipoMedicion) && (
                            <td style={{ ...td, textAlign: "right", color: "#10b981" }}>
                              {formatCurrency(totalesPreview?.sumMonto || 0, data.moneda)}
                              {periodosEditCount > 0 && (
                                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>
                                  original {formatCurrency(data.montoTotal, data.moneda)}
                                </div>
                              )}
                            </td>
                          )}
                          {muestraUnidades(tipoMedicion) && (
                            <td style={{ ...td, textAlign: "right", color: "#3b82f6" }}>
                              {formatNumber(totalesPreview?.sumUnidades || 0, 2)}
                            </td>
                          )}
                          <td style={td}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB CATEGORIAS */}
              {tab === "categorias" && (
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
                  <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
                    <i className="mdi mdi-tag-multiple-outline me-2 text-primary" />
                    Desglose por categoría · {data.categorias?.length || 0}
                  </h6>
                  {(data.categorias || []).length === 0 ? (
                    <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                      <i className="mdi mdi-tag-off-outline" style={{ fontSize: 36, color: "#cbd5e1" }} />
                      <div className="mt-2">No hay categorías asignadas.</div>
                      <div style={{ fontSize: 12, marginTop: 8 }}>
                        El editor avanzado de categorías estará en la próxima versión.
                      </div>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={th}>Código</th>
                            <th style={th}>Nombre</th>
                            <th style={th}>Tipo</th>
                            <th style={th}>Periodo</th>
                            {muestraMonto(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Monto</th>}
                            {muestraUnidades(tipoMedicion) && <th style={{ ...th, textAlign: "right" }}>Unidades</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(data.categorias || []).map((c, i) => (
                            <tr key={c.id} style={{ background: i % 2 ? "#fafbfc" : "#fff" }}>
                              <td style={{ ...td, fontFamily: "monospace" }}>{c.codigoCategoria}</td>
                              <td style={{ ...td, fontWeight: 600 }}>{c.nombreCategoria}</td>
                              <td style={td}>{c.tipoCategoria || "—"}</td>
                              <td style={td}>{c.periodoId ? "Periodo específico" : "Todo el presupuesto"}</td>
                              {muestraMonto(tipoMedicion) && <td style={{ ...td, textAlign: "right" }}>{formatCurrency(c.montoObjetivo, data.moneda)}</td>}
                              {muestraUnidades(tipoMedicion) && <td style={{ ...td, textAlign: "right" }}>{formatNumber(c.unidadesObjetivo, 2)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* TAB VERSIONES */}
              {tab === "versiones" && (
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
                  <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
                    <i className="mdi mdi-source-branch me-2 text-primary" />
                    Versiones del presupuesto
                  </h6>
                  <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>
                    <strong>Esta versión:</strong> v{data.version}
                    {data.presupuestoPadreId
                      ? <> · descendiente de versión raíz <code style={{ background: "#f1f5f9", padding: "0 4px", borderRadius: 3 }}>{data.presupuestoPadreId.substring(0, 8)}</code></>
                      : " · es la raíz"}
                  </div>
                  {(data.versiones || []).length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                      No hay otras versiones de este presupuesto.
                    </div>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {(data.versiones || []).map(v => {
                        const vcfg = estadoConfig(v.estado)
                        return (
                          <li key={v.id} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "8px 10px", borderRadius: 6,
                            background: v.id === data.id ? "#eff6ff" : "transparent",
                            marginBottom: 4, fontSize: 13,
                          }}>
                            <span style={{ fontWeight: 700, color: "#1e40af" }}>v{v.version}</span>
                            <span style={{ flex: 1 }}>{v.nombre}</span>
                            <span style={{
                              padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                              background: vcfg.bg, color: vcfg.color,
                            }}>{vcfg.label}</span>
                            <span style={{ fontSize: 11, color: "#94a3b8" }}>{formatFechaHora(v.createdAt)}</span>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}

              {/* TAB HISTORIAL */}
              {tab === "historial" && (
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
                  <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
                    <i className="mdi mdi-history me-2 text-primary" />
                    Historial de cambios (últimos 100)
                  </h6>
                  {loadingHistorial ? (
                    <div style={{ padding: 40, textAlign: "center" }}>
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : historial.length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Sin movimientos registrados.</div>
                  ) : (
                    <div style={{ maxHeight: 400, overflowY: "auto" }}>
                      {historial.map(h => (
                        <div key={h.id} style={{ borderLeft: "3px solid #3b82f6", paddingLeft: 10, marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{h.accion}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{formatFechaHora(h.createdAt)} · Usuario: {h.usuarioId?.substring(0, 8) || "—"}</div>
                          {h.comentario && <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{h.comentario}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  )
}

// ───────── UI helpers ─────────
const statBox = { flex: "1 1 180px", minWidth: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }
const statLabel = { fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }
const statValue = { fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }
const th = { background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }
const td = { padding: "8px 10px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#0f172a", verticalAlign: "middle" }
const inputStyle = { fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid #e2e8f0", width: 130, textAlign: "right" }

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
    <div style={{ fontWeight: 600 }}>{children}</div>
  </div>
)
Field.propTypes = { label: PropTypes.string, children: PropTypes.node }

DrawerDetalle.propTypes = {
  abierto: PropTypes.bool,
  presupuestoId: PropTypes.string,
  onCerrar: PropTypes.func.isRequired,
  onCambio: PropTypes.func,
}

export default DrawerDetalle
